/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { Col, Row } from "react-awesome-styled-grid";
import { Else, If, Then } from "react-if";
import { ListLoader, Text, theme } from "@nordcloud/gnui";
import {
  Plan,
  PlansFilterByInput,
  PlansFilterByFields,
  PlanSortFields,
  SortOrder,
} from "~/generated/graphql";
import { LoaderWrap } from "~/components/Loader";
import { NoData } from "~/components/NoData";
import { Pagination } from "~/components/Pagination";
import { getNoSearchResultsMessage } from "~/components/SearchInput";
import { useQueryState, useGetSearchPhrase } from "~/hooks";
import { isEmpty } from "~/tools";
import { createFilterByInput } from "~/utils/createFilterByInput";
import { useGetPlans } from "~/views/plans/hooks";
import { ResourceGroupPlansQuery } from "../types";
import { PlansGridColumns } from "./PlansGridColumns";
import { PlansItem } from "./PlansItem";
import { PlansLoader } from "./PlansLoader";

type Props = {
  resourceGroupId: string;
};

const prepareFilterBy = (
  _: ResourceGroupPlansQuery,
  resourceGroupId: string
): PlansFilterByInput[] | undefined => {
  const planResourceGroupFilter = createFilterByInput<PlansFilterByFields>(
    PlansFilterByFields.ResourceGroupId,
    [resourceGroupId],
    false
  );

  return [...planResourceGroupFilter];
};

export function PlansTable({ resourceGroupId }: Props) {
  const { queryState } = useQueryState<ResourceGroupPlansQuery>();
  const { phrase, isSearchAllowed } = useGetSearchPhrase(
    queryState.searchPhrase
  );

  const {
    data: plans,
    loading,
    count,
  } = useGetPlans({
    page: queryState.page,
    limit: queryState.limit,
    filter: {
      filterBy: prepareFilterBy(queryState, resourceGroupId),
      phrase: phrase,
    },
    sort: {
      field: queryState.field ?? PlanSortFields.Name,
      order: queryState.order ?? SortOrder.Asc,
    },
  });

  return (
    <>
      <If condition={!isSearchAllowed}>
        <Then>
          <NoData hasIcon message={getNoSearchResultsMessage()} />
        </Then>
        <Else>
          <LoaderWrap
            loading={loading}
            Component={() => (
              <ListLoader>
                <PlansLoader />
              </ListLoader>
            )}
          >
            <If condition={isEmpty(plans)}>
              <Then>
                <NoData hasIcon message={"No associated plans found."} />
              </Then>
              <Else>
                <Row>
                  <Col>
                    <PlansGridColumns hasPadding>
                      <div />
                      <Text
                        size="sm"
                        m={0}
                        weight="medium"
                        color={theme.color.text.text02}
                      >
                        Plan Name
                      </Text>
                      <Text
                        size="sm"
                        m={0}
                        weight="medium"
                        color={theme.color.text.text02}
                      >
                        Next Execution (local time)
                      </Text>
                      <Text
                        size="sm"
                        m={0}
                        weight="medium"
                        color={theme.color.text.text02}
                      >
                        Next Execution (plan time)
                      </Text>
                    </PlansGridColumns>
                  </Col>
                </Row>
                {(plans as Plan[]).map((plan) => (
                  <PlansItem key={plan.id} plan={plan} />
                ))}
                <Pagination count={count} />
              </Else>
            </If>
          </LoaderWrap>
        </Else>
      </If>
    </>
  );
}
